/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import {
  type ContentStackProductFinder,
  type ContentStackProductFinderColumn,
} from '../../interface';
import InfoToolTip from '@/components/AZCustomComponent/InfoToolTip';
import { View, Text } from '@az/starc-ui';
import { CMSImage } from '../CMSImage/CMSImage';
import styles from './CMSProductFinder.module.scss';
import { Warning } from './Warning/Warning';
import { useCMSProductFinderAttributes } from '../../hooks/useCMSProductFinderAttributes';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import cx from 'classnames';

type Props = {
  content: NonNullable<ContentStackProductFinder>;
};

export const CMSProductFinder = ({ content }: Props) => {
  const { background_image, description = '', heading = '', warning_message } = content;
  const { specificationsAttributes, totalNumberOfRecords } = useCMSProductFinderAttributes();
  const { acesEnabled } = useAcesFlags();

  if (!totalNumberOfRecords || !specificationsAttributes?.length) {
    return null;
  }

  return (
    <View className={cx(styles.productFinderContainer, { [styles.addMarginTop]: acesEnabled })}>
      <View padding={5} direction="column">
        <View.Item>
          <Text variant="display-4" textCase="uppercase" weight="medium" className={styles.header}>
            {heading}
          </Text>
        </View.Item>
        <View.Item gapBefore={2}>
          <View
            direction={{ s: 'column', m: 'column', l: 'row', xl: 'row' }}
            className={styles.flexWrap}
          >
            <View.Item className={styles.descriptionContainer}>
              <View direction="column">
                <Text
                  variant="small-body"
                  className={styles.description}
                  attributes={{ dangerouslySetInnerHTML: { __html: description } }}
                >
                  {null}
                </Text>
                {warning_message && (
                  <View.Item gapBefore={{ s: 3, m: 2, l: 2 }} grow>
                    <Warning warning={warning_message} />
                  </View.Item>
                )}
              </View>
            </View.Item>
            <View.Item
              className={styles.specificationsListContainer}
              grow={{ xl: true }}
              gapBefore={{ s: 3, m: 2, l: 2 }}
            >
              <View
                as="ul"
                direction={{ s: 'column', m: 'row', l: 'row' }}
                align="center"
                width={{ s: '100%', m: '100%', l: '100%' }}
                wrap={false}
                className={styles.specificationsList}
                justify={{ s: 'start', m: 'center', l: 'start' }}
              >
                {specificationsAttributes?.map(({ attribute, description, value }, index) => {
                  const cmsAttribute = content.column?.[index];
                  const showTrailingDivider = index < specificationsAttributes?.length - 1;
                  return (
                    <CMSProductFinderSpecification
                      {...{ attribute, description, value, cmsAttribute, showTrailingDivider }}
                      key={`${attribute ?? 'attribute'} - ${index}`}
                    />
                  );
                })}
              </View>
            </View.Item>
          </View>
        </View.Item>
        {background_image?.image_url && (
          <CMSImage image={background_image} className={styles.backgroundImage} />
        )}
      </View>
    </View>
  );
};

export const CMSProductFinderSpecification = ({
  attribute,
  description,
  value,
  cmsAttribute,
  showTrailingDivider,
}: {
  attribute?: string | null;
  description?: string | null;
  value?: string | null;
  cmsAttribute?: ContentStackProductFinderColumn;
  showTrailingDivider: boolean;
}) => {
  return (
    <>
      <View
        as="li"
        direction="column"
        align="center"
        justify="center"
        className={styles.attributeColumn}
      >
        <View>
          <Text
            align="center"
            color={'primary'}
            weight="bold"
            variant="large-body"
            className={styles.attributeValue}
          >
            {value}
          </Text>
        </View>
        {cmsAttribute?.image && cmsAttribute.image.image_url && (
          <View.Item gapBefore={1}>
            <View className={styles.textless}>
              <CMSImage image={cmsAttribute.image} className={styles.attributeImage} />
            </View>
          </View.Item>
        )}
        <View.Item gapBefore={1}>
          <View direction="column" align="center">
            <View direction="row" align="center">
              <Text variant="small-body" align="center" className={styles.attributeBottomText}>
                {attribute}
              </Text>
              {(cmsAttribute?.tooltip_header || cmsAttribute?.tooltip_column) && (
                <View padding={[0, 0, 0, 1]} justify="center">
                  <InfoToolTip
                    iconSizePx={18}
                    header={cmsAttribute.tooltip_header ?? ''}
                    body={cmsAttribute.tooltip_column ?? ''}
                    placement="top"
                  />
                </View>
              )}
            </View>
            {description && (
              <View>
                <Text className={styles.attributeDescription}>{description}</Text>
              </View>
            )}
          </View>
        </View.Item>
      </View>
      {/* TODO - refactor to CSS dividers ::before, ::after once UI is correct for all breakpoints/# of cols */}
      {showTrailingDivider && (
        <View attributes={{ 'aria-hidden': 'true' }} className={styles.attributeDivider} />
      )}
    </>
  );
};
